<template>
  <div id="SearchPageBlock" class="search">
    <div class="search-header">
      <slot name="heading"></slot>
      <div class="layout-row search-header__row">
        <InputField
          v-model="searchTerm"
          :name="'searchField'"
          additionalClasses="input-field--grey"
          :iconLeft="'icon icon-search'"
          :placeholder="'Search'"
          @update:inputValue="onSearchInput"
          @keyDownEnter="onSearchClick"
        />
        <button
          role="button"
          aria-label="Run a search"
          class="button button--icon"
          @click="onSearchClick"
        >
          <i class="icon icon-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>
    <div v-if="isSearchClicked && !errorMessage">
      <p class="m-b--0">
        Your search for <strong>"{{ searchTermClicked }}"</strong> returned
        {{ results.length }} results
      </p>
      <div v-if=" results.length > 0" class="search-header__filter">
        <p class="m-b--0">Filter by</p>
        <DropdownSelect
          :optionsArray="types"
          :modelValue="filterTypeObject"
          @update:modelValue="updateFilterType"
        />
      </div>
    </div>
    <div
      v-if="
        errorMessage ||
        (isSearchClicked &&
          searchTermClicked &&
          (!results.length || results.length === 0))
      "
      class="search__no-results"
    >
      <p v-if="errorMessage" class="m-b--0">{{ errorMessage }}</p>
      <div v-if="!errorMessage">
        <hr />
      </div>
      <div v-if="!errorMessage" class="search__message">
        <slot name="errormessage"></slot>
      </div>
    </div>
    <div
      v-else-if="isSearchClicked && results && results.length > 0"
      class="search-results"
    >
      <div class="search-results__list">
        <ul>
          <li v-for="result in resultsToShow" :key="result.title">
            <div class="article-card">
              <div class="article-card__inner">
                <div class="article-card__content">
                  <div class="article-card__tag">
                    {{ getGlobalTypeName(result) }}
                  </div>
                  <h4 v-html="highlightSearchTerm(result.title)"></h4>
                  <p v-html="highlightSearchTerm(result.globalDescription)"></p>
                  <a
                    class="article-card__link"
                    aria-label="Go to page"
                    :href="result.link"
                    >{{ result.link }}</a
                  >
                </div>
                <picture v-if="result.imageURL">
                  <img :src="result.imageURL" alt="search card image" />
                </picture>
              </div>
            </div>
          </li>
        </ul>
        <button
          v-if="resultsToShow.length < results.length"
          role="button"
          aria-label="Show more results"
          class="button button--outlined"
          @click="loadMoreResults"
        >
          Show more
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, watchEffect } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import optiClient from '../../services/api';
import DropdownSelect from '../DropdownSelect/DropdownSelect.vue';
import InputField from '../InputField/InputField.vue';

export default {
  name: 'GlobalSearch',
  components: {
    DropdownSelect,
    InputField,
  },
  props: {
    initialSearchTerm: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const searchTerm = ref(props.initialSearchTerm);
    const filterTypeObject = ref({});
    const types = ref([]);
    const results = ref([]);
    const resultsToShow = ref([]);
    const resultsIndex = ref(0);
    const isSearchClicked = ref(false);
    const searchTermClicked = ref('');
    const errorMessage = ref('');
    const pageSize = ref(10);

    const fetchResults = async (searchTerm, filterType, pageSize) => {
      try {
        const data =
          await optiClient.search.getApiV1FindGlobalSearchAndFiltering(
            searchTerm,
            filterType,
            pageSize,
          );

        if (data.data && data.data.length > 0) {
          const searchTerms = searchTerm.toLowerCase().split(' ');
          const filteredResults = data.data.filter((result) => {
            return (
              searchTerms.some((term) =>
                result.title.toLowerCase().includes(term),
              ) &&
              (!filterType ||
                (result.globalType && result.globalType.id === filterType))
            );
          });

          return filteredResults.length > 0 ? filteredResults : [];
        } else {
          return [];
        }
      } catch (error) {
        throw new Error('An error occurred while fetching the results.');
      }
    };

    const searchQuery = useQuery(
      ['searchResults', searchTerm, filterTypeObject.value?.value, pageSize],
      () =>
        fetchResults(searchTerm.value, filterTypeObject.value?.value, pageSize),
      {
        enabled: false,
        onSuccess: (data) => {
          results.value = data;
          resultsToShow.value = data.slice(0, pageSize.value);
          resultsIndex.value = pageSize.value;
          searchTermClicked.value = searchTerm.value;
          errorMessage.value = '';
        },
        onError: (error) => {
          errorMessage.value = error.message;
        },
      },
    );

    const getGlobalTypeName = (result) => {
      if (result.globalType) {
        return result.globalType.name;
      } else {
        return '';
      }
    };

    const onSearchClick = () => {
      if (searchTerm.value.length < 3) {
        errorMessage.value = 'Search term must be at least 3 characters long.';
        results.value = [];
        return;
      }

      isSearchClicked.value = true;
      searchTermClicked.value = '';
      results.value = [];
      resultsToShow.value = [];
      resultsIndex.value = 0;
      errorMessage.value = '';

      searchQuery.refetch();
    };

    const highlightSearchTerm = (text) => {
      if (!searchTermClicked.value || !text) return text;

      let highlightedText = text;
      const searchTerms = searchTermClicked.value.split(' ');

      searchTerms.forEach((term) => {
        const termRegex = new RegExp(term, 'gi');
        highlightedText = highlightedText.replace(
          termRegex,
          '<span class="highlight">$&</span>',
        );
      });

      return highlightedText;
    };

    const loadMoreResults = () => {
      resultsToShow.value = resultsToShow.value.concat(
        results.value.slice(
          resultsToShow.value.length,
          resultsToShow.value.length + pageSize.value,
        ),
      );
    };

    const updateFilterType = ($event) => {
      filterTypeObject.value = $event;
      const filterTypeId = $event.value;
      if (searchTerm.value && searchTerm.value.length >= 3) {
        searchQuery.refetch({ filterType: filterTypeId });
      }
    };

    onMounted(() => {
      useQuery(
        ['availableTypes'],
        () => optiClient.search.getApiV1FindGlobalAvailableTypes(),
        {
          onSuccess: (data) => {
            const allOption = { label: 'All types', value: '' };
            types.value = [allOption].concat(
              data.data.map((type) => ({
                label: type.name,
                value: type.id,
              })),
            ); 
            filterTypeObject.value = allOption;
          },
          onError: () => {
            errorMessage.value =
              'An error occurred while fetching the available types.';
          },
          refetchOnWindowFocus: false
        },
      );
    });

    watchEffect(() => {
      if (props.initialSearchTerm !== '') {
        onSearchClick();
      }
    });

    watch(isSearchClicked, () => {
      if (isSearchClicked.value && searchTerm.value.length < 3) {
        errorMessage.value = 'Search term must be at least 3 characters long.';
        results.value = [];
      } else {
        errorMessage.value = '';
      }
    });

    watch(filterTypeObject, () => {
      if (searchTerm.value && searchTerm.value.length >= 3) {
        searchQuery.refetch();
      }
    });

    return {
      searchTerm,
      filterTypeObject,
      types,
      results,
      resultsToShow,
      resultsIndex,
      isSearchClicked,
      searchTermClicked,
      errorMessage,
      onSearchClick,
      loadMoreResults,
      highlightSearchTerm,
      updateFilterType,
      getGlobalTypeName,
    };
  },
  methods: {
    onSearchInput(newValue) {
      this.searchTerm = newValue;
    },
  },
};
</script>
